import {
  addressesToLookUp,
  connectionActions,
  type AddressEntry,
  type AddressState,
} from './connection-actions';

import type { WikiPerson } from '../people-wiki.types';
import type { LittlebirdAccountsAnswer } from './connections.query';

/** The cards above the composer, in the order that they are shown, one at a time. Each is named for the state of the
 * address that it acts on, and an address in any other state needs nothing from the user: a teammate, a connection, a
 * request that is out, an invite that went out, and an address that the lookup has not answered for.
 * - `accept`: the person asked to connect with the user
 * - `connect`: the person is on Littlebird, and the user has not asked
 * - `invite`: the person has no Littlebird account */
export type CardKind = Extract<AddressState, 'accept' | 'connect' | 'invite'>;

const CARD_KINDS: readonly CardKind[] = ['accept', 'connect', 'invite'];

/** Marks the card in the page, so that the card can tell whether the focus is on it */
export const CARD_SELECTOR = '[data-tagged-people-card]';

/** A tagged person, with the address that the card acts on */
export type TaggedPerson = { person: WikiPerson; entry: AddressEntry };

export type Card = { kind: CardKind; people: TaggedPerson[] };

/** Whether the action of the card is under way, or went through and the card shows that for a moment */
export type CardStatus = 'idle' | 'sending' | 'done';

/** The card to show for the tagged people, or null when none needs the user. Each person lands on the card of the state
 * that matters most among their addresses, as it does for the button on their page. `answer` is the last answer of the
 * lookup, or null while there is none and when the lookup could not. It can be the answer for fewer addresses than the
 * people have, while a lookup of the new ones runs: a person with an address that it was not asked about is unanswered,
 * and has no card, so that no one is shown an Invite before the server says that they are not on Littlebird. */
export const cardForTaggedPeople = (
  people: WikiPerson[],
  answer: LittlebirdAccountsAnswer | null
): Card | null => {
  const taggedPeople = people.flatMap((person) => {
    const isAnswered = addressesToLookUp(person).every((email) =>
      answer?.asked.includes(email)
    );
    const [entry] = connectionActions(
      person,
      isAnswered && answer ? answer.emails : null
    );
    return entry ? { person, entry } : [];
  });
  for (const kind of CARD_KINDS) {
    const cardPeople = taggedPeople.filter(({ entry }) => entry.state === kind);
    if (cardPeople.length > 0) return { kind, people: cardPeople };
  }
  return null;
};

const MAX_NAMES = 2;

/** Up to two names, then the number of the others: "Sam", "Sam and Ana", "Sam, Ana and 3 others" */
export const listNames = (names: string[]): string => {
  const shown = names.slice(0, MAX_NAMES);
  const others = names.length - shown.length;
  if (others === 0) return shown.join(' and ');
  return `${shown.join(', ')} and ${others} ${others === 1 ? 'other' : 'others'}`;
};

const AGENT_FEATURES =
  'This enables features like direct agent-to-agent communication.';

type CardCopy = {
  title: string;
  description?: string;
  actionLabel: string;
  /** What the card says for a moment after the action went through */
  doneLabel: string;
};

export const cardCopy = (kind: CardKind, names: string[]): CardCopy => {
  const list = listNames(names);
  const isPlural = names.length > 1;
  switch (kind) {
    case 'accept':
      return {
        title: `${list} ${isPlural ? 'want' : 'wants'} to connect with you.`,
        actionLabel: 'Accept',
        doneLabel: 'Connected',
      };
    case 'connect':
      return {
        title: `${list} ${isPlural ? 'are' : 'is'} on Littlebird, but you aren't connected.`,
        description: `Would you like to connect? ${AGENT_FEATURES}`,
        actionLabel: 'Connect',
        doneLabel: 'Request sent',
      };
    case 'invite':
      return {
        title: `${list} ${isPlural ? "aren't" : "isn't"} on Littlebird.`,
        description: `Would you like to invite them? ${AGENT_FEATURES}`,
        actionLabel: 'Invite',
        doneLabel: 'Invite sent',
      };
  }
};
